import { sha256Text } from "../../common.js";
import {
  noopCheckpointer,
} from "./checkpointer.js";
import type {
  Checkpointer,
  CheckpointRecord,
  ExtractTask,
  FnTask,
  LlmTask,
  LoomReport,
  LoomResult,
  LoomStep,
  LoomTree,
  ModelRequest,
  ModelResponse,
  ParcelNode,
  ReduceTask,
  RootNode,
  RunOptions,
  ScopeName,
  ScopeNode,
  SplitSpec,
} from "./types.js";

export {
  fileCheckpointer,
  noopCheckpointer,
} from "./checkpointer.js";
export type {
  Checkpointer,
  CheckpointRecord,
  ExtractTask,
  FnTask,
  LlmTask,
  LoomReport,
  LoomResult,
  LoomTree,
  ModelRequest,
  ModelResponse,
  ParcelNode,
  ReduceTask,
  RootNode,
  RunOptions,
  ScopeName,
  ScopeNode,
  SplitSpec,
} from "./types.js";

type TreeNode = ScopeNode<unknown, unknown> | ParcelNode<unknown>;
type WorkNode = ScopeNode<unknown, unknown> | ParcelNode<unknown>;

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map((v) => stableStringify(v)).join(",")}]`;
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

function signature(value: unknown): string {
  return sha256Text(stableStringify(value));
}

function checkpointRecord<T>(key: string, sig: string, value: T): CheckpointRecord<T> {
  return {
    key,
    signature: sig,
    value,
    createdAt: new Date().toISOString(),
  };
}

function joinPrompt(instruction: string, format: string): string {
  return [instruction.trim(), format.trim()].filter(Boolean).join("\n\n");
}

function renderLlmRequest(task: LlmTask<unknown, unknown>, parcelValue: unknown): { system: string; user: string } {
  return {
    system: joinPrompt(task.instruction, task.format),
    user: task.input ? task.input({ parcel: parcelValue }) : String(parcelValue),
  };
}

function extractCheckpointPayload(
  task: ExtractTask<unknown, unknown>,
  parcelValue: unknown,
  runtime: RequiredRuntime,
): { payload: unknown; request?: { system: string; user: string } } {
  if (task.kind === "llm") {
    if (!runtime.executor) throw new Error("loom llm task requires run({ executor })");
    const request = renderLlmRequest(task, parcelValue);
    const route = runtime.executor.route(task.taskId);
    return {
      request,
      payload: {
        step: "extract",
        kind: "llm",
        taskId: task.taskId,
        version: task.version ?? "",
        routeFingerprint: route.fingerprint,
        system: request.system,
        user: request.user,
        salt: runtime.checkpointSalt,
      },
    };
  }
  return {
    payload: {
      step: "extract",
      kind: "fn",
      version: task.version ?? "",
      parcel: parcelValue,
      salt: runtime.checkpointSalt,
    },
  };
}

export function root<TParcel>(
  children: Array<ScopeNode<unknown, TParcel> | ParcelNode<TParcel>>,
): RootNode<TParcel> {
  return { kind: "root", children };
}

export function group<TScope, TParcel>(
  scope: ScopeName,
  children: Array<ScopeNode<unknown, TParcel> | ParcelNode<TParcel>>,
  data: TScope,
): ScopeNode<TScope, TParcel> {
  return { kind: "scope", scope, data, children };
}

export function parcel<TParcel>(data: TParcel): ParcelNode<TParcel> {
  return { kind: "parcel", data };
}

export function llm<TInput, TOutput>(opts: {
  taskId: LlmTask<unknown, unknown>["taskId"];
  version?: string;
  instruction: string;
  format: string;
  input?: (ctx: { parcel: TInput }) => string;
  parse: (ctx: { text: string; parcel: TInput; raw?: unknown }) => TOutput;
  validate?: (output: TOutput) => void;
}): LlmTask<TInput, TOutput> {
  return { kind: "llm", ...opts };
}

export function fn<TInput, TOutput>(opts: {
  version?: string;
  run: (ctx: { parcel: TInput }) => Promise<TOutput> | TOutput;
  validate?: (output: TOutput) => void;
}): FnTask<TInput, TOutput> {
  return { kind: "fn", ...opts };
}

export class LoomJob<TInput, TCurrent> {
  constructor(
    private readonly input: TInput,
    private readonly steps: LoomStep[] = [],
  ) {}

  split<TScope, TParcel>(spec: SplitSpec<TInput, TScope, TParcel>): LoomJob<TInput, TParcel> {
    return new LoomJob<TInput, TParcel>(this.input, [...this.steps, { kind: "split", spec: spec as SplitSpec<unknown, unknown, unknown> }]);
  }

  extract<TOutput>(task: ExtractTask<TCurrent, TOutput>): LoomJob<TInput, TOutput> {
    return new LoomJob<TInput, TOutput>(this.input, [...this.steps, { kind: "extract", task: task as ExtractTask<unknown, unknown> }]);
  }

  reduce<TOutput, TScope = unknown>(
    scope: ScopeName,
    task: ReduceTask<TCurrent, TOutput, TScope> | ((items: TCurrent[], ctx: { scope: TScope | null; scopeName: ScopeName }) => Promise<TOutput> | TOutput),
  ): LoomJob<TInput, TOutput> {
    const normalized: ReduceTask<TCurrent, TOutput, TScope> = typeof task === "function" ? { run: task } : task;
    return new LoomJob<TInput, TOutput>(this.input, [
      ...this.steps,
      { kind: "reduce", scope, task: normalized as ReduceTask<unknown, unknown, unknown> },
    ]);
  }

  plan(): { input: TInput; steps: LoomStep[] } {
    return { input: this.input, steps: [...this.steps] };
  }

  async run(opts: RunOptions = {}): Promise<LoomResult<TCurrent>> {
    const result = await executePlan<TCurrent>(this.input, this.steps, opts);
    return result;
  }
}

export function loom<TInput>(input: TInput): LoomJob<TInput, TInput> {
  return new LoomJob<TInput, TInput>(input);
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return [];
  const out = new Array<R>(items.length);
  let cursor = 0;
  const limit = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: limit }, async () => {
    while (true) {
      const idx = cursor++;
      if (idx >= items.length) break;
      out[idx] = await worker(items[idx]!, idx);
    }
  }));
  return out;
}

function splitTree<TInput>(input: TInput, spec: SplitSpec<unknown, unknown, unknown>): RootNode<unknown> {
  const scopes = spec.partition(input);
  return root(scopes.map((scopeData) => {
    const chunks = spec.chunk(scopeData).map((chunk) => parcel(chunk));
    return group(spec.scope, chunks, scopeData);
  }));
}

function flattenParcels(node: RootNode<unknown> | WorkNode, path: string[] = []): Array<{ parcel: unknown; path: string[] }> {
  if (node.kind === "parcel") return [{ parcel: node.data, path }];
  const children = node.kind === "root" ? node.children : node.children;
  const out: Array<{ parcel: unknown; path: string[] }> = [];
  for (let i = 0; i < children.length; i++) {
    const child = children[i]!;
    const childPath = child.kind === "scope"
      ? [...path, `${child.scope}:${i}`]
      : [...path, `parcel:${i}`];
    out.push(...flattenParcels(child, childPath));
  }
  return out;
}

function validateOutput<T>(validate: ((output: T) => void) | undefined, output: T): T {
  validate?.(output);
  return output;
}

async function runFnExtractTask(
  task: FnTask<unknown, unknown>,
  parcelValue: unknown,
): Promise<unknown> {
  return validateOutput(task.validate, await task.run({ parcel: parcelValue }));
}

async function runLlmModel(
  task: LlmTask<unknown, unknown>,
  request: { system: string; user: string },
  runtime: RequiredRuntime,
): Promise<ModelResponse> {
  if (!runtime.executor) throw new Error("loom llm task requires run({ executor })");
  return runtime.executor.generateText(task.taskId, { system: request.system, user: request.user, signal: runtime.signal });
}

function parseLlmResponse(
  task: LlmTask<unknown, unknown>,
  parcelValue: unknown,
  response: ModelResponse,
): unknown {
  return validateOutput(
    task.validate,
    task.parse({ text: response.text, parcel: parcelValue, raw: response.raw }),
  );
}

function replaceParcels(
  node: RootNode<unknown> | WorkNode,
  values: unknown[],
  cursor: { index: number },
): RootNode<unknown> | WorkNode {
  if (node.kind === "parcel") {
    const value = values[cursor.index];
    cursor.index += 1;
    return parcel(value);
  }
  const children = node.children.map((child) => replaceParcels(child, values, cursor) as WorkNode);
  if (node.kind === "root") return root(children);
  return group(node.scope, children, node.data);
}

function allChildrenParcels(node: ScopeNode<unknown, unknown> | RootNode<unknown>): boolean {
  return node.children.every((child) => child.kind === "parcel");
}

async function reduceScopeNode(
  node: RootNode<unknown> | WorkNode,
  scope: ScopeName,
  task: ReduceTask<unknown, unknown, unknown>,
  runtime: RequiredRuntime,
  path: string[],
): Promise<RootNode<unknown> | WorkNode> {
  if (node.kind === "parcel") return node;
  const reducedChildren: WorkNode[] = [];
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i]!;
    reducedChildren.push(await reduceScopeNode(child, scope, task, runtime, [...path, `${child.kind}:${i}`]) as WorkNode);
  }

  const rebuilt = node.kind === "root" ? root(reducedChildren) : group(node.scope, reducedChildren, node.data);
  const isTargetRoot = scope === "root" && rebuilt.kind === "root";
  const isTargetScope = rebuilt.kind === "scope" && rebuilt.scope === scope;
  if (!isTargetRoot && !isTargetScope) return rebuilt;
  if (!allChildrenParcels(rebuilt)) return rebuilt;

  const items = rebuilt.children.map((child) => (child as ParcelNode<unknown>).data);
  const scopeData = rebuilt.kind === "scope" ? rebuilt.data : null;
  const scopeName = rebuilt.kind === "scope" ? rebuilt.scope : "root";
  const version = task.version ?? "";
  const key = `reduce:${scope}:${path.join("/")}:${signature({ items, scopeData, version, salt: runtime.checkpointSalt })}`;
  const sig = signature({ step: "reduce", scope, version, items, scopeData, salt: runtime.checkpointSalt });
  const cached = await readCheckpoint(runtime.checkpoint, key, sig);
  if (cached.hit) {
    runtime.report.reused += 1;
    return parcel(validateOutput(task.validate, cached.value));
  }
  const out = validateOutput(task.validate, await task.run(items, { scope: scopeData, scopeName }));
  runtime.report.ran += 1;
  await runtime.checkpoint.write(checkpointRecord(key, sig, out));
  return parcel(out);
}

async function readCheckpoint(
  checkpoint: Checkpointer,
  key: string,
  sig: string,
): Promise<{ hit: true; value: unknown } | { hit: false }>;
async function readCheckpoint<T>(
  checkpoint: Checkpointer,
  key: string,
  sig: string,
): Promise<{ hit: true; value: T } | { hit: false }>;
async function readCheckpoint<T = unknown>(
  checkpoint: Checkpointer,
  key: string,
  sig: string,
): Promise<{ hit: true; value: T } | { hit: false }> {
  const record = await checkpoint.read<T>(key);
  if (record && record.signature === sig) return { hit: true, value: record.value };
  return { hit: false };
}

interface RequiredRuntime {
  executor?: RunOptions["executor"];
  signal?: AbortSignal;
  checkpointSalt?: unknown;
  checkpoint: Checkpointer;
  concurrency: number;
  report: LoomReport;
}

async function executePlan<T>(input: unknown, steps: LoomStep[], opts: RunOptions): Promise<LoomResult<T>> {
  const runtime: RequiredRuntime = {
    executor: opts.executor,
    signal: opts.signal,
    checkpointSalt: opts.checkpointSalt,
    checkpoint: opts.checkpoint ?? noopCheckpointer(),
    concurrency: opts.concurrency ?? 4,
    report: { reused: 0, ran: 0 },
  };
  let tree: RootNode<unknown> | null = null;
  let currentInput: unknown = input;
  for (const step of steps) {
    if (step.kind === "split") {
      tree = splitTree(currentInput, step.spec);
      currentInput = tree;
      continue;
    }
    if (!tree) {
      tree = root([parcel(currentInput)]);
    }
    if (step.kind === "extract") {
      const leaves = flattenParcels(tree);
      const values = await mapWithConcurrency(leaves, runtime.concurrency, async (leaf) => {
        const checkpoint = extractCheckpointPayload(step.task, leaf.parcel, runtime);
        const sig = signature(checkpoint.payload);
        const key = `extract:${leaf.path.join("/")}:${sig}`;

        if (step.task.kind === "llm") {
          const cached = await readCheckpoint<ModelResponse>(runtime.checkpoint, key, sig);
          if (cached.hit) {
            runtime.report.reused += 1;
            runtime.executor?.recordReuse(step.task.taskId);
            return parseLlmResponse(step.task, leaf.parcel, cached.value);
          }
          const request = checkpoint.request ?? renderLlmRequest(step.task, leaf.parcel);
          const response = await runLlmModel(step.task, request, runtime);
          const value = parseLlmResponse(step.task, leaf.parcel, response);
          runtime.report.ran += 1;
          await runtime.checkpoint.write(checkpointRecord(key, sig, response));
          return value;
        }

        const cached = await readCheckpoint(runtime.checkpoint, key, sig);
        if (cached.hit) {
          runtime.report.reused += 1;
          return validateOutput(step.task.validate, cached.value);
        }
        const value = await runFnExtractTask(step.task, leaf.parcel);
        runtime.report.ran += 1;
        await runtime.checkpoint.write(checkpointRecord(key, sig, value));
        return value;
      });
      tree = replaceParcels(tree, values, { index: 0 }) as RootNode<unknown>;
      currentInput = tree;
      continue;
    }
    tree = await reduceScopeNode(tree, step.scope, step.task, runtime, []) as RootNode<unknown>;
    currentInput = tree;
  }
  if (!tree) return { value: currentInput as T, report: runtime.report };
  const leaves = flattenParcels(tree);
  if (leaves.length !== 1) {
    throw new Error(`loom plan ended with ${leaves.length} result(s); reduce to root before run completes`);
  }
  return { value: leaves[0]!.parcel as T, report: runtime.report };
}
